import { getOfflineStorage, OfflineStorage } from './storage';
import { getNetworkService, NetworkService } from './network';
import { getSyncService, SyncService } from './sync';
import {
    createCredentialCacheRecord,
    createSessionCacheRecord,
    credentialKey,
    getCachedSessionData,
    getCachedSessionRecord,
    isCredentialUsable,
    isAutoLoginUsable,
    normalizeSessionUser,
    resolveCachedUserId,
    toBool
} from './auth-session';
import $ from 'jquery';

interface OfflineHandler {
    read?: (params: any, storage: OfflineStorage) => Promise<any>;
    write?: (params: any, storage: OfflineStorage, sync: SyncService, baseuri: string, namespace: string, funcName: string) => Promise<any>;
    cache?: (result: any, params: any, storage: OfflineStorage) => Promise<void>;
}

const OFFLINE_LOGIN_UNAVAILABLE_MESSAGE = '오프라인 로그인 캐시가 현재 웹/앱 브라우저에 없습니다. 온라인 상태에서 같은 웹 브라우저 또는 앱에서 같은 계정으로 ID/PW 로그인 또는 ISRI 통합 로그인을 한 번 완료한 뒤 다시 시도하세요.';
const LOGIN_SERVER_UNREACHABLE_MESSAGE = '로그인 서버에 연결하지 못했습니다. 온라인 상태라면 앱/브라우저를 완전히 새로고침한 뒤 다시 로그인해 주세요.';
const DEFAULT_REQUEST_TIMEOUT_MS = 10000;
const AUTO_LOGIN_KEY = 'inspectpro.login.auto';

function storedAutoLoginPreference(): boolean {
    try {
        return localStorage.getItem(AUTO_LOGIN_KEY) === 'true';
    } catch {
        return false;
    }
}

async function restoreAutoLoginSessionForUser(userId: string, storage: OfflineStorage): Promise<any | null> {
    const cachedSession = await getCachedSessionRecord(storage);
    const cachedUserId = resolveCachedUserId(cachedSession);
    const identifiers = [cachedUserId, cachedSession?.email, cachedSession?.mail]
        .map(value => String(value || '').trim())
        .filter(Boolean);
    if (!identifiers.includes(userId)) return null;
    if (!isAutoLoginUsable(cachedSession)) return null;

    const sessionUserId = cachedUserId || userId;
    const session = normalizeSessionUser(sessionUserId, cachedSession);
    const offlineSession = createSessionCacheRecord(sessionUserId, {
        ...session,
        authenticated_at: Date.now()
    }, {
        autoLogin: true,
        autoLoginExpiresAt: cachedSession.auto_login_expires_at,
        authMode: 'cache',
        offline: true,
        online: false
    });
    const previousUserId = await getCurrentUserId(storage);
    if (previousUserId && previousUserId.toLocaleLowerCase() !== sessionUserId.toLocaleLowerCase()) {
        await clearSharedResponseCaches();
    }
    await storage.put('auth', offlineSession);
    return {
        ...offlineSession,
        id: offlineSession.user_id || offlineSession.login_id || sessionUserId
    };
}

async function credentialDigest(userId: string, passwordHash: string): Promise<string> {
    const value = `${userId}:${passwordHash}`;
    const cryptoApi = (window as any).crypto;
    if (!cryptoApi || !cryptoApi.subtle || typeof TextEncoder === 'undefined') {
        return value;
    }

    const bytes = new TextEncoder().encode(value);
    const digest = await cryptoApi.subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(digest))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
}

function parseJson(value: any, fallback: any) {
    if (value === null || value === undefined || value === '') return fallback;
    if (typeof value !== 'string') return value;
    try {
        return JSON.parse(value);
    } catch {
        return fallback;
    }
}

type ChecklistResponseStatus = 'draft' | 'submit_pending' | 'submitted' | 'sync_failed';

function normalizeChecklistStatus(status: any): ChecklistResponseStatus {
    if (status === 'submitted') return 'submitted';
    if (status === 'submit_pending') return 'submit_pending';
    if (status === 'sync_failed') return 'sync_failed';
    return 'draft';
}

function isLocalChecklistStatus(status: any): boolean {
    const normalized = normalizeChecklistStatus(status);
    return normalized === 'submit_pending' || normalized === 'sync_failed';
}

function countChecklistPhotos(answers: any): number {
    if (!answers || typeof answers !== 'object') return 0;
    let count = 0;
    for (const answer of Object.values(answers)) {
        const photos = (answer && typeof answer === 'object') ? (answer as any).photos : null;
        if (Array.isArray(photos)) count += photos.length;
    }
    return count;
}

function getChecklistPhotoSyncStatus(status: ChecklistResponseStatus, answers: any): string {
    const photoCount = countChecklistPhotos(answers);
    if (photoCount === 0) return 'none';
    if (status === 'submitted') return 'synced';
    if (status === 'submit_pending') return 'pending';
    if (status === 'sync_failed') return 'failed';
    return 'local';
}

function checklistResponseKey(responseId: string) {
    return `checklist_response:${responseId}`;
}

function checklistResultKey(responseId: string) {
    return `checklist_result:${responseId}`;
}

function checklistMappingKey(mappingId: string) {
    return `checklist_mapping_response:${mappingId}`;
}

function inspectionViewerSyncKey(drawingId: string) {
    return `inspection_viewer_sync:${drawingId}`;
}

function inspectionViewerOverviewKey(facilityId: string) {
    return `inspection_viewer_overview:${facilityId}`;
}

function selectedProjectsKey(userId: string) {
    return `selected_projects:${userId || 'unknown'}`;
}

function checklistTimestamp() {
    return new Date().toISOString().slice(0, 19).replace('T', ' ');
}

function currentUserIdFromAuth(user: any): string {
    return resolveCachedUserId(user);
}

async function getCurrentUserId(storage: OfflineStorage): Promise<string> {
    const user = await storage.get('auth', 'current_user');
    return currentUserIdFromAuth(user);
}

function userIdFromResult(result: any): string {
    const data = result && result.data ? result.data : {};
    if (!data || typeof data !== 'object' || Array.isArray(data)) return '';
    return String(data.user_id || currentUserIdFromAuth(data.user) || '').trim();
}

function projectIdOf(project: any): string {
    return String((project && (project.id || project.project_id)) || '').trim();
}

function parseProjectDate(value: any): Date | null {
    if (!value) return null;
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return new Date(value.getFullYear(), value.getMonth(), value.getDate());
    }

    const text = String(value).slice(0, 10);
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
    if (!match) return null;

    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    return Number.isNaN(date.getTime()) ? null : date;
}

function normalizeProjectPeriod(project: any): any {
    const next = { ...(project || {}) };
    const startDate = parseProjectDate(next.start_date);
    const endDate = parseProjectDate(next.end_date);

    if (!startDate || !endDate) {
        next.period_status = next.period_status || '-';
        next.status_color = next.status_color || 'none';
        return next;
    }

    const nowValue = new Date();
    const today = new Date(nowValue.getFullYear(), nowValue.getMonth(), nowValue.getDate()).getTime();
    const start = startDate.getTime();
    const end = endDate.getTime();

    if (today < start) {
        next.period_status = '예정';
        next.status_color = 'planned';
    } else if (today >= start && today <= end) {
        next.period_status = '진행중';
        next.status_color = 'ongoing';
    } else {
        next.period_status = '완료';
        next.status_color = 'completed';
    }

    return next;
}

function extractSelectedProjects(result: any): any[] | null {
    if (!result || result.code !== 200) return null;
    const data = result.data;
    if (Array.isArray(data)) return data;
    if (!data || typeof data !== 'object') return null;

    const keys = ['projects', 'selected_projects', 'selectedProjects'];
    for (const key of keys) {
        if (Array.isArray(data[key])) return data[key];
    }
    return null;
}

async function getSelectionRowsForUser(storage: OfflineStorage, userId: string): Promise<any[]> {
    if (!userId) return [];
    let exact: any[] = [];
    try {
        exact = await storage.getAllByIndex('user_selections', 'user_id', userId);
    } catch {}
    const target = String(userId).trim().toLocaleLowerCase();
    const all = await storage.getAll('user_selections');
    const merged = new Map<string, any>();
    for (const selection of [...exact, ...all]) {
        if (String(selection?.user_id || '').trim().toLocaleLowerCase() !== target) continue;
        merged.set(String(selection?.id || `${selection?.user_id}_${selection?.project_id}`), selection);
    }
    return [...merged.values()];
}

async function isCachedProjectSelected(storage: OfflineStorage, projectId: any): Promise<boolean> {
    const userId = await getCurrentUserId(storage);
    const normalizedProjectId = normalizeCacheId(projectId);
    if (!userId || !normalizedProjectId) return false;
    const selections = await getSelectionRowsForUser(storage, userId);
    return selections.some((selection: any) => normalizeCacheId(selection?.project_id) === normalizedProjectId);
}

function cachedAggregateProjectId(cached: any): string {
    return normalizeCacheId(
        cached?.project?.id
        || cached?.facility?.project_id
        || cached?.response?.project_id
        || cached?.project_id
    );
}

async function getCachedProjectById(storage: OfflineStorage, projectId: any): Promise<any> {
    const direct = await storage.get('projects', projectId);
    if (direct) return direct;
    const normalized = String(projectId || '').trim();
    if (!normalized || normalized === projectId) return null;
    return storage.get('projects', normalized);
}

function normalizeCacheId(value: any): string {
    return String(value || '').trim();
}

async function getCachedRecordById(storage: OfflineStorage, storeName: string, id: any): Promise<any> {
    const normalized = normalizeCacheId(id);
    if (!normalized) return null;
    const direct = await storage.get(storeName, id);
    if (direct) return direct;
    if (normalized !== id) return storage.get(storeName, normalized);
    return null;
}

async function getCachedRowsByIndex(storage: OfflineStorage, storeName: string, indexName: string, value: any): Promise<any[]> {
    const normalized = normalizeCacheId(value);
    if (!normalized) return [];
    try {
        return await storage.getAllByIndex(storeName, indexName, normalized);
    } catch {
        const rows = await storage.getAll(storeName);
        return rows.filter((row: any) => normalizeCacheId(row && row[indexName]) === normalized);
    }
}

function facilityDrawingKey(facilityId: any, drawingId: any): string {
    return `${normalizeCacheId(facilityId)}_${normalizeCacheId(drawingId)}`;
}

async function cacheFacilityDrawingMapping(storage: OfflineStorage, facilityId: any, drawingId: any): Promise<void> {
    const normalizedFacilityId = normalizeCacheId(facilityId);
    const normalizedDrawingId = normalizeCacheId(drawingId);
    if (!normalizedFacilityId || !normalizedDrawingId) return;
    await storage.put('facility_drawings', {
        id: facilityDrawingKey(normalizedFacilityId, normalizedDrawingId),
        facility_id: normalizedFacilityId,
        drawing_id: normalizedDrawingId,
        cached_at: Date.now()
    });
}

function drawingIdOf(value: any): string {
    if (!value) return '';
    if (typeof value === 'string' || typeof value === 'number') return normalizeCacheId(value);
    return normalizeCacheId(value.id || value.drawing_id);
}

function drawingListFromFacility(facility: any): any[] {
    if (Array.isArray(facility?.drawings)) return facility.drawings;
    const parsed = parseJson(facility?.drawing_ids, []);
    if (Array.isArray(parsed)) return parsed;
    return [];
}

function hasCachedFileData(file: any): boolean {
    return !!(file && (file.data || file.blob));
}

async function mergeAndCacheFacility(storage: OfflineStorage, facility: any): Promise<any> {
    if (!facility || !facility.id) return null;
    const existing = await getCachedRecordById(storage, 'facilities', facility.id);
    const merged = {
        ...(existing || {}),
        ...facility
    };
    if (!Array.isArray(merged.overviewPhotos) && Array.isArray(existing?.overviewPhotos)) {
        merged.overviewPhotos = existing.overviewPhotos;
    }
    await storage.put('facilities', merged);
    return merged;
}

async function hasCachedFacilityDrawingMapping(
    storage: OfflineStorage,
    facilityId: any,
    drawingId: any
): Promise<boolean> {
    const normalizedFacilityId = normalizeCacheId(facilityId);
    const normalizedDrawingId = normalizeCacheId(drawingId);
    if (!normalizedFacilityId || !normalizedDrawingId) return false;
    const direct = await getCachedRecordById(
        storage,
        'facility_drawings',
        facilityDrawingKey(normalizedFacilityId, normalizedDrawingId)
    );
    if (direct
        && normalizeCacheId(direct.facility_id) === normalizedFacilityId
        && normalizeCacheId(direct.drawing_id) === normalizedDrawingId) {
        return true;
    }
    const relations = await getCachedRowsByIndex(
        storage,
        'facility_drawings',
        'facility_id',
        normalizedFacilityId
    );
    return relations.some((relation: any) =>
        normalizeCacheId(relation?.drawing_id) === normalizedDrawingId
    );
}

function cacheValueReferencesAny(value: any, ids: Set<string>): boolean {
    if (!value || ids.size === 0) return false;
    let serialized = '';
    try { serialized = JSON.stringify(value); } catch {}
    if (!serialized) return false;
    return [...ids].some((id: string) => !!id && serialized.includes(id));
}

function cacheValueReferencesPair(
    value: any,
    pair: { facilityId: string; drawingId: string }
): boolean {
    if (!value || !pair) return false;
    let serialized = '';
    try { serialized = JSON.stringify(value); } catch {}
    return !!serialized
        && serialized.includes(pair.facilityId)
        && serialized.includes(pair.drawingId);
}

function cachedChecklistKey(item: any): string {
    return normalizeCacheId(item?.mapping_id || item?.id || item?.checklist_id);
}

function addCachedChecklistReferences(facility: any, ids: Set<string>): void {
    const checklists = Array.isArray(facility?.checklists) ? facility.checklists : [];
    for (const checklist of checklists) {
        const mappingId = normalizeCacheId(checklist?.mapping_id || checklist?.id);
        const responseId = normalizeCacheId(checklist?.response_id);
        if (mappingId) ids.add(mappingId);
        if (responseId) ids.add(responseId);
    }
}

export async function reconcileProjectSnapshotCache(
    storage: OfflineStorage,
    data: any,
    options: {
        scope?: 'merge' | 'project_full' | 'facility_list' | 'facility_links';
        projectId?: any;
        facilityId?: any;
    } = { scope: 'merge' }
): Promise<void> {
    if (!data || typeof data !== 'object') return;
    const scope = String(options?.scope || 'merge');
    const projectId = normalizeCacheId(
        options?.projectId || data?.project?.id || data?.facilities?.[0]?.project_id
    );
    if (!projectId) return;
    const facilitiesAuthoritative = (
        scope === 'project_full' || scope === 'facility_list'
    ) && Array.isArray(data.facilities);
    const drawingsAuthoritative = scope === 'project_full' && Array.isArray(data.drawings);
    const linksAuthoritative = scope === 'project_full' && Array.isArray(data.facility_drawings);
    if (!facilitiesAuthoritative
        && !drawingsAuthoritative
        && !linksAuthoritative
        && scope !== 'facility_links') return;

    const existingFacilities = await getCachedRowsByIndex(storage, 'facilities', 'project_id', projectId);
    const existingDrawings = await getCachedRowsByIndex(storage, 'drawings', 'project_id', projectId);
    const remoteFacilities = facilitiesAuthoritative ? data.facilities : [];
    const remoteDrawings = drawingsAuthoritative ? data.drawings : [];
    const remoteFacilityIds = new Set<string>(
        remoteFacilities.map((item: any) => normalizeCacheId(item?.id)).filter(Boolean)
    );
    const remoteDrawingIds = new Set<string>(
        remoteDrawings.map((item: any) => normalizeCacheId(item?.id || item?.drawing_id)).filter(Boolean)
    );
    const projectFacilityIds = new Set<string>([
        ...existingFacilities.map((item: any) => normalizeCacheId(item?.id)),
        ...remoteFacilityIds
    ].filter(Boolean));
    const projectDrawingIds = new Set<string>([
        ...existingDrawings.map((item: any) => normalizeCacheId(item?.id)),
        ...remoteDrawingIds
    ].filter(Boolean));
    const staleFacilities = facilitiesAuthoritative
        ? existingFacilities.filter((item: any) => !remoteFacilityIds.has(normalizeCacheId(item?.id)))
        : [];
    const staleDrawings = drawingsAuthoritative
        ? existingDrawings.filter((item: any) => !remoteDrawingIds.has(normalizeCacheId(item?.id)))
        : [];
    const staleFacilityIds = new Set<string>(
        staleFacilities.map((item: any) => normalizeCacheId(item?.id)).filter(Boolean)
    );
    const staleDrawingIds = new Set<string>(
        staleDrawings.map((item: any) => normalizeCacheId(item?.id)).filter(Boolean)
    );
    const relatedIds = new Set<string>([...staleFacilityIds, ...staleDrawingIds]);
    for (const facility of staleFacilities) addCachedChecklistReferences(facility, relatedIds);

    if (facilitiesAuthoritative) {
        const cachedById = new Map<string, any>(
            existingFacilities.map((item: any) => [normalizeCacheId(item?.id), item])
        );
        for (const facility of remoteFacilities) {
            if (!Array.isArray(facility?.checklists)) continue;
            const cached = cachedById.get(normalizeCacheId(facility?.id));
            const oldChecklists = Array.isArray(cached?.checklists) ? cached.checklists : [];
            const nextByKey = new Map<string, any>(
                facility.checklists.map((item: any) => [cachedChecklistKey(item), item])
            );
            for (const oldChecklist of oldChecklists) {
                const key = cachedChecklistKey(oldChecklist);
                const nextChecklist = nextByKey.get(key);
                const oldMappingId = normalizeCacheId(oldChecklist?.mapping_id || oldChecklist?.id);
                const oldResponseId = normalizeCacheId(oldChecklist?.response_id);
                if (!nextChecklist) {
                    if (oldMappingId) relatedIds.add(oldMappingId);
                    if (oldResponseId) relatedIds.add(oldResponseId);
                    continue;
                }
                const nextResponseId = normalizeCacheId(nextChecklist?.response_id);
                if (oldResponseId && oldResponseId !== nextResponseId) relatedIds.add(oldResponseId);
            }
        }
    }

    const remotePairs = new Set<string>();
    const perFacilityLinkScopes = new Set<string>();
    if (linksAuthoritative) {
        for (const relation of data.facility_drawings) {
            const facilityId = normalizeCacheId(relation?.facility_id);
            const drawingId = normalizeCacheId(relation?.drawing_id);
            if (facilityId && drawingId) remotePairs.add(facilityDrawingKey(facilityId, drawingId));
        }
    } else if (scope === 'project_full' && facilitiesAuthoritative) {
        for (const facility of remoteFacilities) {
            const facilityId = normalizeCacheId(facility?.id);
            if (!facilityId || !Array.isArray(facility?.drawings)) continue;
            perFacilityLinkScopes.add(facilityId);
            for (const drawing of facility.drawings) {
                const drawingId = drawingIdOf(drawing);
                if (drawingId) remotePairs.add(facilityDrawingKey(facilityId, drawingId));
            }
        }
    } else if (scope === 'facility_links') {
        const facilityId = normalizeCacheId(options?.facilityId || data?.facilities?.[0]?.id);
        if (facilityId && Array.isArray(data.drawings)) {
            perFacilityLinkScopes.add(facilityId);
            for (const drawing of data.drawings) {
                const drawingId = drawingIdOf(drawing);
                if (drawingId) remotePairs.add(facilityDrawingKey(facilityId, drawingId));
            }
        }
    }

    const existingRelations = await storage.getAll('facility_drawings');
    const removedPairs: { facilityId: string; drawingId: string }[] = [];
    for (const relation of existingRelations) {
        const facilityId = normalizeCacheId(relation?.facility_id);
        const drawingId = normalizeCacheId(relation?.drawing_id);
        const pairKey = facilityDrawingKey(facilityId, drawingId);
        const hasStaleEndpoint = staleFacilityIds.has(facilityId) || staleDrawingIds.has(drawingId);
        const belongsToProject = projectFacilityIds.has(facilityId) || projectDrawingIds.has(drawingId);
        const missingAuthoritativeLink = (
            linksAuthoritative && belongsToProject && !remotePairs.has(pairKey)
        ) || (
            perFacilityLinkScopes.has(facilityId) && !remotePairs.has(pairKey)
        );
        if ((hasStaleEndpoint || missingAuthoritativeLink) && relation?.id != null) {
            await storage.delete('facility_drawings', relation.id);
            removedPairs.push({ facilityId, drawingId });
        }
    }

    if (relatedIds.size === 0 && removedPairs.length === 0) return;
    const metaRows = await storage.getAll('meta');
    const ownedPrefixes = new Set([
        'checklist_response',
        'checklist_result',
        'checklist_mapping_response',
        'inspection_viewer_sync',
        'inspection_viewer_overview'
    ]);
    for (let pass = 0; pass < 2; pass++) {
        for (const item of metaRows) {
            if (!cacheValueReferencesAny(item, relatedIds)) continue;
            const key = normalizeCacheId(item?.key);
            const prefix = key.includes(':') ? key.split(':', 1)[0] : '';
            if (ownedPrefixes.has(prefix)) {
                const keyId = normalizeCacheId(key.split(':').slice(1).join(':'));
                if (keyId) relatedIds.add(keyId);
            }
            const value = item?.value || {};
            const mappingId = normalizeCacheId(value?.response?.mapping_id || value?.mapping_id);
            const responseId = normalizeCacheId(value?.response?.id || value?.response_id);
            if (mappingId) relatedIds.add(mappingId);
            if (responseId) relatedIds.add(responseId);
        }
    }

    for (const facility of staleFacilities) {
        if (facility?.id != null) await storage.delete('facilities', facility.id);
    }
    for (const drawing of staleDrawings) {
        if (drawing?.id == null) continue;
        await storage.delete('drawings', drawing.id);
        await storage.delete('damages', drawing.id);
        await storage.delete('annotations', drawing.id);
    }
    for (const file of await storage.getAll('files')) {
        if (cacheValueReferencesAny(file, relatedIds) && file?.id != null) {
            await storage.delete('files', file.id);
        }
    }
    for (const item of await storage.getAll('sync_queue')) {
        const remove = cacheValueReferencesAny(item, relatedIds)
            || removedPairs.some(pair => cacheValueReferencesPair(item, pair));
        if (remove && item?.id != null) await storage.delete('sync_queue', item.id);
    }
    for (const item of metaRows) {
        if (cacheValueReferencesAny(item, relatedIds) && item?.key != null) {
            await storage.delete('meta', item.key);
        }
    }

    const manifest = await storage.getCacheManifest(projectId);
    if (manifest) {
        const items = Array.isArray(manifest.items) ? manifest.items : [];
        const nextItems = items.filter(item =>
            !cacheValueReferencesAny(item, relatedIds)
            && !removedPairs.some(pair => cacheValueReferencesPair(item, pair))
        );
        if (nextItems.length !== items.length) {
            await storage.setCacheManifest({
                ...manifest,
                items: nextItems,
                updated_at: Date.now()
            });
        }
    }

    try {
        if (typeof caches !== 'undefined') {
            for (const cacheName of await caches.keys()) {
                const cache = await caches.open(cacheName);
                for (const request of await cache.keys()) {
                    const url = String(request.url || '');
                    const remove = [...relatedIds].some((id: string) =>
                        url.includes(encodeURIComponent(id)) || url.includes(id)
                    ) || removedPairs.some(pair =>
                        (url.includes(encodeURIComponent(pair.facilityId)) || url.includes(pair.facilityId))
                        && (url.includes(encodeURIComponent(pair.drawingId)) || url.includes(pair.drawingId))
                    );
                    if (remove) await cache.delete(request);
                }
            }
        }
    } catch (error) {
        console.warn('[OfflineInterceptor] M-Pro snapshot Cache API cleanup failed:', error);
    }
}

async function cacheProjectDetailData(
    storage: OfflineStorage,
    data: any,
    options: {
        scope?: 'merge' | 'project_full' | 'facility_list' | 'facility_links';
        projectId?: any;
        facilityId?: any;
    } = { scope: 'merge' }
): Promise<void> {
    if (!data || typeof data !== 'object') return;
    await reconcileProjectSnapshotCache(storage, data, options);
    if (data.project && data.project.id) await storage.put('projects', data.project);
    if (Array.isArray(data.drawings) && data.drawings.length > 0) await storage.putMany('drawings', data.drawings);
    if (Array.isArray(data.facility_drawings)) {
        for (const row of data.facility_drawings) {
            const facilityId = row?.facility_id;
            const drawingId = row?.drawing_id;
            if (facilityId && drawingId) await cacheFacilityDrawingMapping(storage, facilityId, drawingId);
        }
    }
    if (Array.isArray(data.facilities)) {
        for (const facility of data.facilities) {
            const cachedFacility = await mergeAndCacheFacility(storage, facility);
            for (const drawing of drawingListFromFacility(facility)) {
                const drawingId = drawingIdOf(drawing);
                if (!drawingId) continue;
                if (drawing && typeof drawing === 'object') await storage.put('drawings', drawing);
                await cacheFacilityDrawingMapping(storage, cachedFacility?.id || facility.id, drawingId);
            }
        }
    }
}

async function getCachedDrawingsForFacility(storage: OfflineStorage, facility: any): Promise<any[]> {
    const facilityId = normalizeCacheId(facility?.id);
    const drawings: any[] = [];
    const seen = new Set<string>();

    const pushDrawing = async (drawingRef: any) => {
        const drawingId = drawingIdOf(drawingRef);
        if (!drawingId || seen.has(drawingId)) return;
        seen.add(drawingId);
        let drawing = drawingRef && typeof drawingRef === 'object' ? drawingRef : null;
        if (!drawing || !drawing.id) drawing = await getCachedRecordById(storage, 'drawings', drawingId);
        if (drawing) drawings.push(drawing);
    };

    if (facilityId) {
        const mappings = await getCachedRowsByIndex(storage, 'facility_drawings', 'facility_id', facilityId);
        for (const mapping of mappings) await pushDrawing(mapping.drawing_id);
    }

    if (drawings.length === 0) {
        for (const drawing of drawingListFromFacility(facility)) await pushDrawing(drawing);
    }

    return drawings;
}

async function readCachedProjectDetail(storage: OfflineStorage, projectId: any): Promise<any> {
    const normalizedProjectId = normalizeCacheId(projectId);
    if (!await isCachedProjectSelected(storage, normalizedProjectId)) {
        return { code: 403, data: null, message: '현재 사용자의 MPRO 배정 캐시에 없는 프로젝트입니다.' };
    }
    const project = await getCachedProjectById(storage, normalizedProjectId);
    if (!project) return { code: 404, data: null, message: '캐시된 프로젝트 데이터가 없습니다.' };

    const drawings = await getCachedRowsByIndex(storage, 'drawings', 'project_id', normalizedProjectId);
    const facilities = await getCachedRowsByIndex(storage, 'facilities', 'project_id', normalizedProjectId);
    for (const facility of facilities) {
        const linkedDrawings = await getCachedDrawingsForFacility(storage, facility);
        facility.drawings = linkedDrawings;
        facility.drawing_count = linkedDrawings.length || Number(facility.drawing_count || 0);
    }

    return {
        code: 200,
        data: {
            project,
            drawings,
            facilities,
            offline: true
        }
    };
}

async function readCachedFacilityDetail(storage: OfflineStorage, facilityId: any): Promise<any> {
    const normalizedFacilityId = normalizeCacheId(facilityId);
    const facility = await getCachedRecordById(storage, 'facilities', normalizedFacilityId);
    if (!facility) return { code: 404, data: null, message: '캐시된 시설물 데이터가 없습니다.' };
    if (!await isCachedProjectSelected(storage, facility.project_id)) {
        return { code: 403, data: null, message: '현재 사용자의 MPRO 배정 캐시에 없는 시설물입니다.' };
    }

    const drawings = await getCachedDrawingsForFacility(storage, facility);
    const overviewPhotos = await storage.getMeta(inspectionViewerOverviewKey(normalizedFacilityId));
    return {
        code: 200,
        data: {
            facility,
            drawings,
            overview_photos: Array.isArray(overviewPhotos) ? overviewPhotos : (facility.overviewPhotos || []),
            offline: true
        }
    };
}

async function writeSelectedProjectSnapshot(
    storage: OfflineStorage,
    userId: string,
    projectIds: string[],
    projects: any[]
): Promise<void> {
    await storage.setMeta(selectedProjectsKey(userId), {
        user_id: userId,
        project_ids: projectIds,
        projects,
        cached_at: Date.now()
    });
}

async function purgeRemovedProjectCache(storage: OfflineStorage, projectId: string): Promise<void> {
    const facilities = await storage.getAllByIndex('facilities', 'project_id', projectId);
    const drawings = await storage.getAllByIndex('drawings', 'project_id', projectId);
    const facilityIds = new Set(facilities.map((item: any) => String(item?.id || '')).filter((id: string) => !!id));
    const drawingIds = new Set(drawings.map((item: any) => String(item?.id || '')).filter((id: string) => !!id));
    const relatedIds = new Set([projectId, ...facilityIds, ...drawingIds]);
    for (const facility of facilities) {
        const checklists = Array.isArray(facility?.checklists) ? facility.checklists : [];
        for (const mapping of checklists) {
            const mappingId = String(mapping?.mapping_id || mapping?.id || '').trim();
            const responseId = String(mapping?.response_id || '').trim();
            if (mappingId) relatedIds.add(mappingId);
            if (responseId) relatedIds.add(responseId);
        }
    }
    const metaRows = await storage.getAll('meta');
    for (const item of metaRows) {
        let serialized = '';
        try { serialized = JSON.stringify(item); } catch {}
        if (![...relatedIds].some((id: string) => serialized.includes(id))) continue;
        const key = String(item?.key || '');
        const keyPrefix = key.includes(':') ? key.split(':', 1)[0] : '';
        const ownedPrefixes = new Set([
            'checklist_response',
            'checklist_result',
            'checklist_mapping_response',
            'inspection_viewer_sync',
            'inspection_viewer_overview'
        ]);
        const keyId = ownedPrefixes.has(keyPrefix)
            ? key.split(':').slice(1).join(':').trim()
            : '';
        const responseId = String(item?.response?.id || item?.response_id || '').trim();
        const mappingId = String(item?.response?.mapping_id || item?.mapping_id || '').trim();
        if (keyId) relatedIds.add(keyId);
        if (responseId) relatedIds.add(responseId);
        if (mappingId) relatedIds.add(mappingId);
    }

    for (const relation of await storage.getAll('facility_drawings')) {
        const facilityId = String(relation?.facility_id || '');
        const drawingId = String(relation?.drawing_id || '');
        if ((facilityIds.has(facilityId) || drawingIds.has(drawingId)) && relation?.id != null) {
            await storage.delete('facility_drawings', relation.id);
        }
    }
    for (const facility of facilities) {
        if (facility?.id != null) await storage.delete('facilities', facility.id);
    }
    for (const drawing of drawings) {
        if (drawing?.id == null) continue;
        await storage.delete('drawings', drawing.id);
        await storage.delete('damages', drawing.id);
        await storage.delete('annotations', drawing.id);
    }
    for (const file of await storage.getAll('files')) {
        const refId = String(file?.ref_id || file?.project_id || '');
        if (relatedIds.has(refId) && file?.id != null) await storage.delete('files', file.id);
    }
    for (const item of await storage.getAll('sync_queue')) {
        const entityId = String(item?.entity_id || '');
        let payload = '';
        try { payload = JSON.stringify(item?.payload || {}); } catch {}
        if ((relatedIds.has(entityId) || [...relatedIds].some((id: string) => payload.includes(id))) && item?.id != null) {
            await storage.delete('sync_queue', item.id);
        }
    }
    for (const item of metaRows) {
        const key = String(item?.key || '');
        let serialized = '';
        try { serialized = JSON.stringify(item); } catch {}
        if ([key, serialized].some((value: string) => [...relatedIds].some((id: string) => value.includes(id))) && item?.key != null) {
            await storage.delete('meta', item.key);
        }
    }

    await storage.delete('projects', projectId);
    await storage.delete('cache_manifests', projectId);

    try {
        if (typeof caches !== 'undefined') {
            for (const cacheName of await caches.keys()) {
                const cache = await caches.open(cacheName);
                for (const request of await cache.keys()) {
                    const url = String(request.url || '');
                    if ([...relatedIds].some((id: string) => url.includes(encodeURIComponent(id)) || url.includes(id))) {
                        await cache.delete(request);
                    }
                }
            }
        }
    } catch (error) {
        console.warn('[OfflineInterceptor] Removed project Cache API cleanup failed:', error);
    }
}

async function cleanupScopedCacheReferences(
    storage: OfflineStorage,
    projectId: string,
    relatedIds: Set<string>,
    removedPairs: { facilityId: string; drawingId: string }[] = []
): Promise<void> {
    const metaRows = await storage.getAll('meta');
    const ownedPrefixes = new Set([
        'checklist_response',
        'checklist_result',
        'checklist_mapping_response',
        'inspection_viewer_sync',
        'inspection_viewer_overview'
    ]);
    for (let pass = 0; pass < 2; pass++) {
        for (const item of metaRows) {
            if (!cacheValueReferencesAny(item, relatedIds)) continue;
            const key = normalizeCacheId(item?.key);
            const prefix = key.includes(':') ? key.split(':', 1)[0] : '';
            if (ownedPrefixes.has(prefix)) {
                const keyId = normalizeCacheId(key.split(':').slice(1).join(':'));
                if (keyId) relatedIds.add(keyId);
            }
            const value = item?.value || {};
            const mappingId = normalizeCacheId(value?.response?.mapping_id || value?.mapping_id);
            const responseId = normalizeCacheId(value?.response?.id || value?.response_id);
            if (mappingId) relatedIds.add(mappingId);
            if (responseId) relatedIds.add(responseId);
        }
    }

    for (const file of await storage.getAll('files')) {
        if (cacheValueReferencesAny(file, relatedIds) && file?.id != null) {
            await storage.delete('files', file.id);
        }
    }
    for (const item of await storage.getAll('sync_queue')) {
        const remove = cacheValueReferencesAny(item, relatedIds)
            || removedPairs.some(pair => cacheValueReferencesPair(item, pair));
        if (remove && item?.id != null) await storage.delete('sync_queue', item.id);
    }
    for (const item of metaRows) {
        if (cacheValueReferencesAny(item, relatedIds) && item?.key != null) {
            await storage.delete('meta', item.key);
        }
    }

    if (projectId) {
        const manifest = await storage.getCacheManifest(projectId);
        if (manifest) {
            const items = Array.isArray(manifest.items) ? manifest.items : [];
            const nextItems = items.filter(item =>
                !cacheValueReferencesAny(item, relatedIds)
                && !removedPairs.some(pair => cacheValueReferencesPair(item, pair))
            );
            if (nextItems.length !== items.length) {
                await storage.setCacheManifest({
                    ...manifest,
                    items: nextItems,
                    updated_at: Date.now()
                });
            }
        }
    }

    try {
        if (typeof caches !== 'undefined') {
            for (const cacheName of await caches.keys()) {
                const cache = await caches.open(cacheName);
                for (const request of await cache.keys()) {
                    const url = String(request.url || '');
                    const remove = [...relatedIds].some((id: string) =>
                        url.includes(encodeURIComponent(id)) || url.includes(id)
                    ) || removedPairs.some(pair =>
                        (url.includes(encodeURIComponent(pair.facilityId)) || url.includes(pair.facilityId))
                        && (url.includes(encodeURIComponent(pair.drawingId)) || url.includes(pair.drawingId))
                    );
                    if (remove) await cache.delete(request);
                }
            }
        }
    } catch (error) {
        console.warn('[OfflineInterceptor] Scoped resource Cache API cleanup failed:', error);
    }
}

async function tombstoneFacilityDrawingLink(
    storage: OfflineStorage,
    facilityId: any,
    drawingId: any
): Promise<void> {
    const normalizedFacilityId = normalizeCacheId(facilityId);
    const normalizedDrawingId = normalizeCacheId(drawingId);
    if (!normalizedFacilityId || !normalizedDrawingId) return;

    const relations = await getCachedRowsByIndex(
        storage,
        'facility_drawings',
        'facility_id',
        normalizedFacilityId
    );
    for (const relation of relations) {
        if (normalizeCacheId(relation?.drawing_id) === normalizedDrawingId && relation?.id != null) {
            await storage.delete('facility_drawings', relation.id);
        }
    }
    const facility = await getCachedRecordById(storage, 'facilities', normalizedFacilityId);
    const projectId = normalizeCacheId(facility?.project_id);
    await cleanupScopedCacheReferences(storage, projectId, new Set<string>(), [
        { facilityId: normalizedFacilityId, drawingId: normalizedDrawingId }
    ]);
}

async function tombstoneFacilityCache(storage: OfflineStorage, facilityId: any): Promise<void> {
    const normalizedFacilityId = normalizeCacheId(facilityId);
    if (!normalizedFacilityId) return;
    const facility = await getCachedRecordById(storage, 'facilities', normalizedFacilityId);
    const projectId = normalizeCacheId(facility?.project_id);
    const relatedIds = new Set<string>([normalizedFacilityId]);
    addCachedChecklistReferences(facility, relatedIds);
    const removedPairs: { facilityId: string; drawingId: string }[] = [];
    for (const relation of await getCachedRowsByIndex(
        storage,
        'facility_drawings',
        'facility_id',
        normalizedFacilityId
    )) {
        const drawingId = normalizeCacheId(relation?.drawing_id);
        if (relation?.id != null) await storage.delete('facility_drawings', relation.id);
        if (drawingId) removedPairs.push({ facilityId: normalizedFacilityId, drawingId });
    }
    if (facility?.id != null) await storage.delete('facilities', facility.id);
    await cleanupScopedCacheReferences(storage, projectId, relatedIds, removedPairs);
}

async function tombstoneDrawingCache(storage: OfflineStorage, drawingId: any): Promise<void> {
    const normalizedDrawingId = normalizeCacheId(drawingId);
    if (!normalizedDrawingId) return;
    const drawing = await getCachedRecordById(storage, 'drawings', normalizedDrawingId);
    const projectId = normalizeCacheId(drawing?.project_id);
    const relatedIds = new Set<string>([normalizedDrawingId]);
    const removedPairs: { facilityId: string; drawingId: string }[] = [];
    for (const relation of await getCachedRowsByIndex(
        storage,
        'facility_drawings',
        'drawing_id',
        normalizedDrawingId
    )) {
        const facilityId = normalizeCacheId(relation?.facility_id);
        if (relation?.id != null) await storage.delete('facility_drawings', relation.id);
        if (facilityId) removedPairs.push({ facilityId, drawingId: normalizedDrawingId });
    }
    if (drawing?.id != null) await storage.delete('drawings', drawing.id);
    await storage.delete('damages', normalizedDrawingId);
    await storage.delete('annotations', normalizedDrawingId);
    await cleanupScopedCacheReferences(storage, projectId, relatedIds, removedPairs);
}

async function tombstoneChecklistResponseCache(
    storage: OfflineStorage,
    responseId: any
): Promise<void> {
    const normalizedResponseId = normalizeCacheId(responseId);
    if (!normalizedResponseId) return;
    const aggregate = await storage.getMeta(checklistResponseKey(normalizedResponseId))
        || await storage.getMeta(checklistResultKey(normalizedResponseId));
    let projectId = cachedAggregateProjectId(aggregate);
    for (const facility of await storage.getAll('facilities')) {
        const checklists = Array.isArray(facility?.checklists) ? facility.checklists : [];
        let changed = false;
        const nextChecklists = checklists.map((checklist: any) => {
            if (normalizeCacheId(checklist?.response_id) !== normalizedResponseId) return checklist;
            changed = true;
            return { ...checklist, response_id: null, response_status: null };
        });
        if (changed) {
            projectId = projectId || normalizeCacheId(facility?.project_id);
            await storage.put('facilities', { ...facility, checklists: nextChecklists });
        }
    }
    await cleanupScopedCacheReferences(
        storage,
        projectId,
        new Set<string>([normalizedResponseId])
    );
}

async function reconcileEndpointResourceTombstone(
    storage: OfflineStorage,
    handlerKey: string,
    result: any,
    params: any
): Promise<void> {
    const code = Number(result?.code || 0);
    if (code !== 403 && code !== 404) return;
    const message = String(
        result?.message || (typeof result?.data === 'string' ? result.data : '') || ''
    );
    const isViewer = handlerKey === 'page.inspection.viewer/get_viewer_data'
        || handlerKey === 'page.inspection.viewer/save';
    if (isViewer) {
        await tombstoneFacilityDrawingLink(storage, params?.facility_id, params?.drawing_id);
        if (code === 404 && /시설물/.test(message)) {
            await tombstoneFacilityCache(storage, params?.facility_id);
        } else if (code === 404 && /도면/.test(message) && !/연결/.test(message)) {
            await tombstoneDrawingCache(storage, params?.drawing_id);
        }
        return;
    }
    if (code !== 404) return;
    if (handlerKey === 'component.project.facilities/get_facility_detail'
        || handlerKey === 'page.project.detail/get_facility_detail') {
        await tombstoneFacilityCache(storage, params?.facility_id || params?.id);
        return;
    }
    if (handlerKey.includes('drawing')) {
        await tombstoneDrawingCache(storage, params?.drawing_id || params?.id);
        return;
    }
    if (handlerKey.startsWith('page.checklist.respond/')
        || handlerKey.startsWith('page.checklist.result/')) {
        await tombstoneChecklistResponseCache(storage, params?.response_id);
    }
}

async function resolveDeniedProjectId(
    storage: OfflineStorage,
    handlerKey: string,
    params: any
): Promise<string> {
    const directProjectId = normalizeCacheId(params?.project_id);
    if (directProjectId) return directProjectId;

    if (handlerKey === 'page.project.detail/detail' || handlerKey === 'page.project.list/detail') {
        return normalizeCacheId(params?.id);
    }

    const facilityId = normalizeCacheId(
        params?.facility_id || (handlerKey.includes('facility') ? params?.id : '')
    );
    if (facilityId) {
        const facility = await getCachedRecordById(storage, 'facilities', facilityId);
        const projectId = normalizeCacheId(facility?.project_id);
        if (projectId) return projectId;
    }

    const drawingId = normalizeCacheId(
        params?.drawing_id
        || (handlerKey.includes('drawing') || handlerKey.includes('/download') ? params?.id : '')
    );
    if (drawingId) {
        const drawing = await getCachedRecordById(storage, 'drawings', drawingId);
        const projectId = normalizeCacheId(drawing?.project_id);
        if (projectId) return projectId;
    }

    let responseId = normalizeCacheId(params?.response_id);
    const mappingId = normalizeCacheId(params?.mapping_id);
    if (!responseId && mappingId) {
        const mapping = await storage.getMeta(checklistMappingKey(mappingId));
        responseId = normalizeCacheId(mapping?.response_id);
    }
    if (responseId) {
        const aggregate = await storage.getMeta(checklistResponseKey(responseId))
            || await storage.getMeta(checklistResultKey(responseId));
        return cachedAggregateProjectId(aggregate);
    }
    return '';
}

function isProjectAssignmentDenial(handlerKey: string, result: any): boolean {
    const code = Number(result?.code || 0);
    if (code !== 403 && code !== 404) return false;
    if (handlerKey.endsWith('/select') || handlerKey.endsWith('/unselect')) return false;
    if (handlerKey === 'page.project.detail/detail' || handlerKey === 'page.project.list/detail') {
        return true;
    }
    const message = String(
        result?.message || (typeof result?.data === 'string' ? result.data : '') || ''
    );
    return /PM|참여자|배정/.test(message) && /접근|조회/.test(message);
}

export async function tombstoneDeniedProjectCache(
    storage: OfflineStorage,
    projectId: string,
    explicitUserId: string = ''
): Promise<void> {
    const normalizedProjectId = normalizeCacheId(projectId);
    const userId = explicitUserId || await getCurrentUserId(storage);
    if (!normalizedProjectId || !userId) return;

    const currentUserKey = String(userId).trim().toLocaleLowerCase();
    for (const selection of await getSelectionRowsForUser(storage, userId)) {
        if (normalizeCacheId(selection?.project_id) === normalizedProjectId && selection?.id != null) {
            await storage.delete('user_selections', selection.id);
        }
    }

    const snapshot = await storage.getMeta(selectedProjectsKey(userId));
    const projectIds = Array.isArray(snapshot?.project_ids)
        ? snapshot.project_ids
            .map((id: any) => normalizeCacheId(id))
            .filter((id: string) => id && id !== normalizedProjectId)
        : [];
    const projects = Array.isArray(snapshot?.projects)
        ? snapshot.projects
            .filter((project: any) => projectIdOf(project) !== normalizedProjectId)
            .map((project: any) => normalizeProjectPeriod(project))
        : [];
    await writeSelectedProjectSnapshot(storage, userId, projectIds, projects);

    const remainingSelections = (await storage.getAll('user_selections')).filter(
        (row: any) => normalizeCacheId(row?.project_id) === normalizedProjectId
    );
    const belongsToAnotherUser = remainingSelections.some(
        (row: any) => String(row?.user_id || '').trim().toLocaleLowerCase() !== currentUserKey
    );
    if (!belongsToAnotherUser) await purgeRemovedProjectCache(storage, normalizedProjectId);
}

async function cacheSelectedProjectList(
    storage: OfflineStorage,
    projects: any[],
    explicitUserId: string = ''
): Promise<void> {
    const userId = explicitUserId || await getCurrentUserId(storage);
    const normalizedProjects = projects
        .filter((project: any) => project && projectIdOf(project))
        .map((project: any) => normalizeProjectPeriod(project));

    if (normalizedProjects.length > 0) {
        await storage.putMany('projects', normalizedProjects);
    }

    if (!userId) return;

    const projectIds: string[] = [];
    const seen = new Set<string>();
    for (const project of normalizedProjects) {
        const projectId = projectIdOf(project);
        if (!projectId || seen.has(projectId)) continue;
        seen.add(projectId);
        projectIds.push(projectId);
        await storage.put('user_selections', {
            id: `${userId}_${projectId}`,
            user_id: userId,
            project_id: projectId,
            source: 'online_selected_projects',
            cached_at: Date.now()
        });
    }

    const existing = await getSelectionRowsForUser(storage, userId);
    for (const selection of existing) {
        const projectId = String(selection.project_id || '').trim();
        if (projectId && !seen.has(projectId)) {
            await storage.delete('user_selections', selection.id);
            const remainingSelections = (await storage.getAll('user_selections')).filter(
                (row: any) => normalizeCacheId(row?.project_id) === projectId
            );
            const currentUserKey = String(userId).trim().toLocaleLowerCase();
            const belongsToAnotherUser = remainingSelections.some(
                (row: any) => String(row?.user_id || '').trim().toLocaleLowerCase() !== currentUserKey
            );
            if (!belongsToAnotherUser) await purgeRemovedProjectCache(storage, projectId);
        }
    }

    await writeSelectedProjectSnapshot(storage, userId, projectIds, normalizedProjects);
}

async function cacheSelectedProjectsFromResult(result: any, storage: OfflineStorage): Promise<void> {
    const projects = extractSelectedProjects(result);
    if (projects === null) return;
    await cacheSelectedProjectList(storage, projects, userIdFromResult(result));
}

async function readCachedSelectedProjects(storage: OfflineStorage): Promise<any[]> {
    const userId = await getCurrentUserId(storage);
    if (!userId) return [];

    const snapshot = await storage.getMeta(selectedProjectsKey(userId));
    const snapshotProjects = Array.isArray(snapshot?.projects) ? snapshot.projects : [];
    const snapshotProjectById: Record<string, any> = {};
    for (const project of snapshotProjects) {
        const projectId = projectIdOf(project);
        if (projectId) snapshotProjectById[projectId] = project;
    }

    let projectIds = (await getSelectionRowsForUser(storage, userId))
        .map((selection: any) => String(selection.project_id || '').trim())
        .filter((projectId: string) => !!projectId);

    if (projectIds.length === 0 && Array.isArray(snapshot?.project_ids)) {
        projectIds = snapshot.project_ids
            .map((projectId: any) => String(projectId || '').trim())
            .filter((projectId: string) => !!projectId);
    }

    if (projectIds.length === 0 && snapshotProjects.length > 0) {
        return snapshotProjects.map((project: any) => normalizeProjectPeriod(project));
    }

    const projects = [];
    const seen = new Set<string>();
    for (const projectId of projectIds) {
        if (seen.has(projectId)) continue;
        seen.add(projectId);
        const project = await getCachedProjectById(storage, projectId) || snapshotProjectById[projectId];
        if (project) projects.push(normalizeProjectPeriod(project));
    }

    return projects;
}

async function addCachedProjectSelection(storage: OfflineStorage, userId: string, projectId: string): Promise<void> {
    if (!userId || !projectId) return;
    await storage.put('user_selections', {
        id: `${userId}_${projectId}`,
        user_id: userId,
        project_id: projectId,
        source: 'local_selection',
        cached_at: Date.now()
    });

    const projects = await readCachedSelectedProjects(storage);
    const project = await getCachedProjectById(storage, projectId);
    if (project && !projects.some((entry: any) => projectIdOf(entry) === projectId)) {
        projects.push(normalizeProjectPeriod(project));
    }
    await writeSelectedProjectSnapshot(storage, userId, projects.map((entry: any) => projectIdOf(entry)), projects);
}

async function removeCachedProjectSelection(storage: OfflineStorage, userId: string, projectId: string): Promise<void> {
    if (!userId || !projectId) return;
    await storage.delete('user_selections', `${userId}_${projectId}`);
    const snapshot = await storage.getMeta(selectedProjectsKey(userId));
    const projectIds = Array.isArray(snapshot?.project_ids)
        ? snapshot.project_ids.map((id: any) => String(id || '').trim()).filter((id: string) => id && id !== projectId)
        : [];
    const projects = Array.isArray(snapshot?.projects)
        ? snapshot.projects.filter((project: any) => projectIdOf(project) !== projectId).map((project: any) => normalizeProjectPeriod(project))
        : [];
    await writeSelectedProjectSnapshot(storage, userId, projectIds, projects);
}

function applyChecklistMeta(response: any, meta: any, status?: string) {
    const next = { ...(response || {}) };
    const allowed = [
        'total_score', 'max_score', 'safety_grade', 'inspector_name',
        'inspection_date', 'overall_opinion', 'emergency_inspection',
        'safety_action'
    ];

    for (const key of allowed) {
        if (meta && meta[key] !== undefined) next[key] = meta[key];
    }

    if (status) next.status = status;
    if (!next.status) next.status = 'draft';
    next.updated = checklistTimestamp();
    return next;
}

async function cacheChecklistAggregate(storage: OfflineStorage, data: any) {
    if (!data || !data.response || !data.response.id) return;
    const responseId = data.response.id;
    const current = await storage.getMeta(checklistResponseKey(responseId));
    const incomingStatus = normalizeChecklistStatus(data.response.status);
    const currentStatus = normalizeChecklistStatus(current && current.response ? current.response.status : '');
    const currentSyncSource = current && current.response ? current.response.sync_source : '';
    const currentSyncStatus = current && current.response ? current.response.sync_status : '';
    const hasUnsyncedLocalDraft = currentStatus === 'draft'
        && currentSyncSource === 'local'
        && currentSyncStatus !== 'synced';
    let next = {
        ...data,
        response: {
            ...data.response,
            status: incomingStatus,
            sync_source: 'server',
            sync_status: 'synced',
            photo_sync_status: data.response.photo_sync_status || getChecklistPhotoSyncStatus(incomingStatus, data.answers)
        }
    };

    if (current && current.response && (isLocalChecklistStatus(currentStatus) || hasUnsyncedLocalDraft) && incomingStatus !== 'submitted') {
        next = {
            ...data,
            response: {
                ...data.response,
                ...current.response,
                status: currentStatus,
                sync_source: 'local',
                sync_status: currentStatus === 'sync_failed' ? 'failed' : (current.response.sync_status || 'pending'),
                photo_sync_status: current.response.photo_sync_status || getChecklistPhotoSyncStatus(currentStatus, current.answers || data.answers)
            },
            answers: current.answers || data.answers
        };
    }

    await storage.setMeta(checklistResponseKey(responseId), next);
    await storage.setMeta(checklistResultKey(responseId), next);
    if (data.response.mapping_id) {
        await storage.setMeta(checklistMappingKey(data.response.mapping_id), {
            response_id: responseId,
            status: next.response.status || 'draft',
            cached_at: Date.now()
        });
    }
    if (data.facility && data.facility.id) await storage.put('facilities', data.facility);
    if (data.project && data.project.id) await storage.put('projects', data.project);
}

async function updateCachedChecklistMapping(storage: OfflineStorage, mappingId: string, responseId: string, status: string) {
    if (!mappingId) return;

    await storage.setMeta(checklistMappingKey(mappingId), {
        response_id: responseId,
        status,
        cached_at: Date.now()
    });

    const facilities = await storage.getAll('facilities');
    for (const facility of facilities) {
        const checklists = Array.isArray(facility.checklists) ? facility.checklists : [];
        let changed = false;
        for (const mapping of checklists) {
            if (mapping && mapping.mapping_id === mappingId) {
                mapping.response_id = responseId;
                mapping.response_status = status;
                changed = true;
            }
        }
        if (changed) await storage.put('facilities', facility);
    }
}

async function findCachedChecklistMapping(storage: OfflineStorage, mappingId: string) {
    const cached = await storage.getMeta(checklistMappingKey(mappingId));
    if (cached && cached.response_id) {
        const aggregate = await storage.getMeta(checklistResponseKey(cached.response_id));
        const projectId = cachedAggregateProjectId(aggregate);
        if (projectId) return { ...cached, project_id: projectId };
    }

    const facilities = await storage.getAll('facilities');
    for (const facility of facilities) {
        const checklists = Array.isArray(facility.checklists) ? facility.checklists : [];
        for (const mapping of checklists) {
            if (mapping && mapping.mapping_id === mappingId && mapping.response_id) {
                return {
                    response_id: mapping.response_id,
                    status: mapping.response_status || 'draft',
                    project_id: mapping.project_id || facility.project_id
                };
            }
        }
    }

    return null;
}

async function applyCachedChecklistMappingStatuses(storage: OfflineStorage, facilities: any[]) {
    if (!Array.isArray(facilities)) return facilities;

    for (const facility of facilities) {
        const checklists = Array.isArray(facility.checklists) ? facility.checklists : [];
        for (const mapping of checklists) {
            if (!mapping || !mapping.mapping_id) continue;
            const cached = await storage.getMeta(checklistMappingKey(mapping.mapping_id));
            if (!cached || !cached.response_id) continue;
            const cachedStatus = normalizeChecklistStatus(cached.status);
            const serverStatus = normalizeChecklistStatus(mapping.response_status);
            if (isLocalChecklistStatus(cachedStatus) || (cachedStatus === 'submitted' && serverStatus !== 'submitted')) {
                mapping.response_id = cached.response_id;
                mapping.response_status = cachedStatus;
            }
        }
    }

    return facilities;
}

async function writeChecklistResponse(
    params: any,
    storage: OfflineStorage,
    sync: SyncService,
    baseuri: string,
    namespace: string,
    funcName: string
) {
    const responseId = String((params && params.response_id) || '').trim();
    if (!responseId) {
        return { code: 400, data: null, message: '응답 ID가 없습니다.' };
    }

    const cached = await storage.getMeta(checklistResponseKey(responseId));
    if (!cached || !cached.response) {
        return {
            code: 503,
            data: null,
            message: '오프라인 저장을 위해서는 이 체크리스트를 온라인에서 한 번 열어야 합니다.'
        };
    }

    const projectId = cachedAggregateProjectId(cached);
    if (!await isCachedProjectSelected(storage, projectId)) {
        return { code: 403, data: null, message: '현재 사용자의 MPRO 배정 캐시에 없는 체크리스트입니다.' };
    }

    const answers = parseJson(params.answers, {});
    const meta = parseJson(params.meta, {});
    const isSubmit = funcName === 'submit_response';
    const cachedStatus = normalizeChecklistStatus(cached.response.status);
    const status = isSubmit
        ? 'submit_pending'
        : (cachedStatus === 'submitted' || cachedStatus === 'submit_pending' ? cachedStatus : 'draft');
    const photoCount = countChecklistPhotos(answers);
    const response = applyChecklistMeta(cached.response, meta, status);
    response.sync_source = 'local';
    response.sync_status = 'pending';
    response.sync_error = '';
    response.photo_count = photoCount;
    response.photo_sync_status = getChecklistPhotoSyncStatus(status, answers);
    if (isSubmit) response.submitted_local_at = checklistTimestamp();

    const next = {
        ...cached,
        response,
        answers
    };

    await storage.setMeta(checklistResponseKey(responseId), next);
    await storage.setMeta(checklistResultKey(responseId), next);
    await updateCachedChecklistMapping(storage, next.response.mapping_id, responseId, status);
    await sync.queueWrite({
        entity_type: 'checklist_response',
        entity_id: responseId,
        action: funcName,
        payload: params,
        api_endpoint: `${baseuri}/api/${namespace}/${funcName}`,
        operation_id: `checklist_response:${responseId}:${funcName}`,
        replace_actions: isSubmit ? ['save_response'] : []
    });

    return {
        code: 200,
        data: {
            saved: 'local',
            status,
            photo_sync_status: response.photo_sync_status,
            response: next.response
        },
        message: isSubmit
            ? '오프라인 제출은 서버 제출 완료가 아니라 제출 동기화 대기 상태로 저장되었습니다.'
            : '오프라인에 임시 저장되었습니다. 네트워크가 복구되면 동기화됩니다.'
    };
}

async function clearSharedResponseCaches(): Promise<void> {
    try {
        if (typeof caches !== 'undefined') {
            for (const cacheName of await caches.keys()) {
                if (cacheName.startsWith('inspectpro-api-') || cacheName.startsWith('inspectpro-pdf-')) {
                    await caches.delete(cacheName);
                }
            }
        }
    } catch (error) {
        console.warn('[OfflineInterceptor] Shared response cache clear failed:', error);
    }
}

const offlineHandlers: Record<string, OfflineHandler> = {
    // === AUTH ===
    'component.auth.login/login': {
        write: async (params, storage) => {
            const userId = String((params && params.id) || '').trim();
            const passwordHash = String((params && params.password) || '');
            if (!userId || !passwordHash) {
                return { code: 401, data: '아이디 또는 비밀번호를 확인해주세요.' };
            }

            const cached = await storage.get('auth', credentialKey(userId));
            const digest = await credentialDigest(userId, passwordHash);
            if (!cached || cached.password_digest !== digest || !isCredentialUsable(cached)) {
                const restoredSession = await restoreAutoLoginSessionForUser(userId, storage);
                if (restoredSession) return { code: 200, data: restoredSession };
                return { code: 401, data: OFFLINE_LOGIN_UNAVAILABLE_MESSAGE };
            }

            const autoLogin = toBool(params && params.auto_login)
                && toBool(cached.auto_login_enabled)
                && Number(cached.auto_login_expires_at || 0) > Date.now();
            const session = normalizeSessionUser(userId, cached.session);
            const offlineSession = createSessionCacheRecord(userId, {
                ...session,
                authenticated_at: Date.now()
            }, {
                autoLogin,
                autoLoginExpiresAt: cached.auto_login_expires_at,
                authMode: 'cache',
                offline: true,
                online: false
            });
            const previousUserId = await getCurrentUserId(storage);
            if (previousUserId && previousUserId.toLocaleLowerCase() !== userId.toLocaleLowerCase()) {
                await clearSharedResponseCaches();
            }
            await storage.put('auth', offlineSession);
            return {
                code: 200,
                data: {
                    ...offlineSession,
                    id: offlineSession.user_id || offlineSession.login_id || userId
                }
            };
        },
        cache: async (result, params, storage) => {
            const userId = String((params && params.id) || '').trim();
            const passwordHash = String((params && params.password) || '');
            if (result && result.code === 200 && userId && passwordHash) {
                const autoLogin = toBool(params && params.auto_login);
                const session = normalizeSessionUser(userId, result.data);
                const current = await getCachedSessionRecord(storage);
                const previousUserId = currentUserIdFromAuth(current);
                if (previousUserId && previousUserId.toLocaleLowerCase() !== userId.toLocaleLowerCase()) {
                    await clearSharedResponseCaches();
                }
                await storage.put('auth', createSessionCacheRecord(userId, session, {
                    autoLogin,
                    existing: current,
                    authMode: 'server',
                    offline: false
                }));
                await storage.put('auth', createCredentialCacheRecord(
                    userId,
                    await credentialDigest(userId, passwordHash),
                    session,
                    { autoLogin, serverPasswordHash: passwordHash }
                ));
            }
        }
    },
    'layout.navbar/session': {
        read: async (params, storage) => {
            const session = await getCachedSessionData(storage);
            if (session) return { code: 200, data: session };
            return { code: 401, data: null };
        },
        cache: async (result, params, storage) => {
            if (result && result.code === 200 && result.data) {
                const userId = result.data.user_id || result.data.login_id || result.data.id || 'current_user';
                const session = normalizeSessionUser(userId, result.data);
                const current = await getCachedSessionRecord(storage);
                const previousUserId = currentUserIdFromAuth(current);
                if (previousUserId && previousUserId.toLocaleLowerCase() !== String(userId).toLocaleLowerCase()) {
                    await clearSharedResponseCaches();
                }
                await storage.put('auth', createSessionCacheRecord(userId, session, {
                    autoLogin: storedAutoLoginPreference(),
                    existing: current,
                    authMode: 'server',
                    offline: false
                }));
            }
        }
    },
    'layout.field/session': {
        read: async (params, storage) => {
            const session = await getCachedSessionData(storage);
            if (session) return { code: 200, data: session };
            return { code: 401, data: null };
        },
        cache: async (result, params, storage) => {
            if (result && result.code === 200 && result.data) {
                const userId = result.data.user_id || result.data.login_id || result.data.id || 'current_user';
                const session = normalizeSessionUser(userId, result.data);
                const current = await getCachedSessionRecord(storage);
                const previousUserId = currentUserIdFromAuth(current);
                if (previousUserId && previousUserId.toLocaleLowerCase() !== String(userId).toLocaleLowerCase()) {
                    await clearSharedResponseCaches();
                }
                await storage.put('auth', createSessionCacheRecord(userId, session, {
                    autoLogin: storedAutoLoginPreference(),
                    existing: current,
                    authMode: 'server',
                    offline: false
                }));
            }
        }
    },

    'layout.navbar/logout': {
        write: async (params, storage) => {
            await storage.delete('auth', 'current_user');
            await clearSharedResponseCaches();
            return { code: 200, data: true, offline: true };
        },
        cache: async (result, params, storage) => {
            if (result && result.code === 200) {
                await storage.delete('auth', 'current_user');
                await clearSharedResponseCaches();
            }
        }
    },
    'layout.field/logout': {
        write: async (params, storage) => {
            await storage.delete('auth', 'current_user');
            await clearSharedResponseCaches();
            return { code: 200, data: true, offline: true };
        },
        cache: async (result, params, storage) => {
            if (result && result.code === 200) {
                await storage.delete('auth', 'current_user');
                await clearSharedResponseCaches();
            }
        }
    },

    // === HOME ===
    'page.home/init': {
        read: async (params, storage) => {
            const session = await getCachedSessionData(storage);
            if (!session) return { code: 401, data: null };
            const selectedProjects = await readCachedSelectedProjects(storage);
            return {
                code: 200,
                data: {
                    user: session,
                    initialized: true,
                    selected_projects: selectedProjects,
                    offline: true
                }
            };
        },
        cache: async (result, params, storage) => {
            if (result && result.code === 200 && result.data && result.data.user) {
                const userId = result.data.user.user_id || result.data.user.login_id || result.data.user.id || 'current_user';
                const session = normalizeSessionUser(userId, result.data.user);
                const current = await getCachedSessionRecord(storage);
                const previousUserId = currentUserIdFromAuth(current);
                if (previousUserId && previousUserId.toLocaleLowerCase() !== String(userId).toLocaleLowerCase()) {
                    await clearSharedResponseCaches();
                }
                await storage.put('auth', createSessionCacheRecord(userId, session, {
                    autoLogin: storedAutoLoginPreference(),
                    existing: current,
                    authMode: 'server',
                    offline: false
                }));
            }
            await cacheSelectedProjectsFromResult(result, storage);
        }
    },

    // === MAIN DASHBOARD ===
    'page.main/data': {
        read: async (params, storage) => {
            const projects = await readCachedSelectedProjects(storage);
            const selected = await readCachedSelectedProjects(storage);

            const byType: any = {};
            for (const p of projects) {
                const t = p.type || '기타';
                if (!byType[t]) byType[t] = [];
                byType[t].push(p);
            }

            return {
                code: 200,
                data: {
                    projects,
                    selected_projects: selected,
                    selectedProjects: selected,
                    by_type: byType,
                    stats: {
                        total: projects.length,
                        진행중: projects.filter((p: any) => p.status === '진행중').length,
                        대기: projects.filter((p: any) => p.status === '대기').length,
                        완료: projects.filter((p: any) => p.status === '완료').length
                    }
                }
            };
        },
        cache: async (result, params, storage) => {
            if (result && result.code === 200 && result.data) {
                const d = result.data;
                if (d.projects) await storage.putMany('projects', d.projects);
                if (d.grouped && typeof d.grouped === 'object') {
                    const groupedProjects = Object.keys(d.grouped).reduce((rows: any[], key: string) => {
                        return Array.isArray(d.grouped[key]) ? rows.concat(d.grouped[key]) : rows;
                    }, []);
                    if (groupedProjects.length > 0) await storage.putMany('projects', groupedProjects);
                }
                await cacheSelectedProjectsFromResult(result, storage);
            }
        }
    },
    'page.main/select': {
        write: async () => ({ code: 403, data: '용역의 PM/참여자 배정은 MPRO에서 변경해 주세요.' })
    },
    'page.main/unselect': {
        write: async () => ({ code: 403, data: '용역의 PM/참여자 배정은 MPRO에서 변경해 주세요.' })
    },

    // === PROJECT LIST ===
    'page.project.list/list': {
        read: async (params, storage) => {
            let projects = await readCachedSelectedProjects(storage);
            const text = params.text || params.search || '';
            const status = params.status || '';
            const type = params.type || '';

            if (text) {
                const q = text.toLowerCase();
                projects = projects.filter((p: any) =>
                    (p.title || '').toLowerCase().includes(q) ||
                    (p.category || '').toLowerCase().includes(q)
                );
            }
            if (status && status !== '전체') projects = projects.filter((p: any) => p.status === status);
            if (type && type !== '전체') projects = projects.filter((p: any) => p.type === type);

            // Add facility counts from cache
            for (const p of projects) {
                const facs = await storage.getAllByIndex('facilities', 'project_id', p.id);
                p.facility_count = facs.length;
            }

            const page = parseInt(params.page || '1');
            const dump = parseInt(params.dump || '20');
            const total = projects.length;
            const start = (page - 1) * dump;
            const rows = projects.slice(start, start + dump);

            return { code: 200, data: { list: rows, rows, total, page, dump } };
        },
        cache: async (result, params, storage) => {
            const rows = result && result.data ? (result.data.rows || result.data.list) : null;
            if (result && result.code === 200) {
                const selectedProjects = result.data?.selected_projects;
                if (Array.isArray(selectedProjects)) {
                    await cacheSelectedProjectList(
                        storage,
                        selectedProjects,
                        String(result.data?.user_id || '')
                    );
                }
                if (rows) await storage.putMany('projects', rows);
            }
        }
    },
    'page.project.list/stats': {
        read: async (params, storage) => {
            const projects = await readCachedSelectedProjects(storage);
            return {
                code: 200,
                data: {
                    total: projects.length,
                    by_status: {
                        대기: projects.filter((p: any) => p.status === '대기').length,
                        진행중: projects.filter((p: any) => p.status === '진행중').length,
                        완료: projects.filter((p: any) => p.status === '완료').length
                    },
                    by_type: projects.reduce((acc: any, p: any) => {
                        acc[p.type || '기타'] = (acc[p.type || '기타'] || 0) + 1;
                        return acc;
                    }, {})
                }
            };
        }
    },

    // === PROJECT DETAIL ===
    'page.project.detail/detail': {
        read: async (params, storage) => {
            return readCachedProjectDetail(storage, params.id);
        },
        cache: async (result, params, storage) => {
            if (result && result.code === 200 && result.data) {
                await cacheProjectDetailData(storage, result.data, { scope: 'project_full' });
            }
        }
    },

    // === INSPECTION ===
    'page.inspection/init': {
        read: async (params, storage) => {
            const projects = await readCachedSelectedProjects(storage);
            return { code: 200, data: { projects } };
        },
        cache: async (result, params, storage) => {
            await cacheSelectedProjectsFromResult(result, storage);
        }
    },

    // === CHECKLIST ===
    'page.checklist/init': {
        read: async (params, storage) => {
            const projects = await readCachedSelectedProjects(storage);
            return { code: 200, data: { projects } };
        },
        cache: async (result, params, storage) => {
            await cacheSelectedProjectsFromResult(result, storage);
        }
    },
    'page.checklist.facilities/load': {
        read: async (params, storage) => {
            const projectId = params.project_id;
            if (!await isCachedProjectSelected(storage, projectId)) {
                return { code: 403, data: null, message: '현재 사용자의 MPRO 배정 캐시에 없는 체크리스트 용역입니다.' };
            }
            const project = await getCachedProjectById(storage, projectId);
            let facilities = await getCachedRowsByIndex(storage, 'facilities', 'project_id', projectId);
            if (facilities.length === 0) {
                const normalizedProjectId = normalizeCacheId(projectId);
                const allFacilities = await storage.getAll('facilities');
                facilities = allFacilities.filter((facility: any) => {
                    if (normalizeCacheId(facility?.project_id) === normalizedProjectId) return true;
                    const checklists = Array.isArray(facility?.checklists) ? facility.checklists : [];
                    return checklists.some((mapping: any) => normalizeCacheId(mapping?.project_id) === normalizedProjectId);
                });
            }
            await applyCachedChecklistMappingStatuses(storage, facilities);
            return { code: 200, data: { project, facilities } };
        },
        cache: async (result, params, storage) => {
            if (result && result.code === 200 && result.data) {
                await cacheProjectDetailData(storage, {
                    project: result.data.project,
                    facilities: result.data.facilities
                }, { scope: 'facility_list' });
                if (Array.isArray(result.data.facilities)) {
                    await applyCachedChecklistMappingStatuses(storage, result.data.facilities);
                    for (const facility of result.data.facilities) await mergeAndCacheFacility(storage, facility);
                }
            }
        }
    },
    'page.checklist.facilities/start_checklist': {
        write: async (params, storage) => {
            const mappingId = String((params && params.mapping_id) || '').trim();
            const cached = mappingId ? await findCachedChecklistMapping(storage, mappingId) : null;
            if (cached && cached.response_id) {
                if (!await isCachedProjectSelected(storage, cached.project_id)) {
                    return { code: 403, data: null, message: '현재 사용자의 MPRO 배정 캐시에 없는 체크리스트입니다.' };
                }
                return {
                    code: 200,
                    data: {
                        response_id: cached.response_id,
                        status: cached.status || 'draft',
                        offline: true
                    }
                };
            }

            return {
                code: 503,
                data: null,
                message: '오프라인에서 새 체크리스트를 시작하려면 온라인에서 먼저 체크리스트를 한 번 열어야 합니다.'
            };
        },
        cache: async (result, params, storage) => {
            const mappingId = String((params && params.mapping_id) || '').trim();
            const responseId = result && result.data ? result.data.response_id : '';
            if (result && result.code === 200 && mappingId && responseId) {
                await storage.setMeta(checklistMappingKey(mappingId), {
                    response_id: responseId,
                    status: result.data.status || 'draft',
                    cached_at: Date.now()
                });
            }
        }
    },
    'page.checklist.respond/load_response': {
        read: async (params, storage) => {
            const responseId = String((params && params.response_id) || '').trim();
            const cached = responseId ? await storage.getMeta(checklistResponseKey(responseId)) : null;
            if (!cached) {
                return {
                    code: 404,
                    data: null,
                    message: '캐시된 체크리스트 응답이 없습니다.'
                };
            }
            if (!await isCachedProjectSelected(storage, cachedAggregateProjectId(cached))) {
                return { code: 403, data: null, message: '현재 사용자의 MPRO 배정 캐시에 없는 체크리스트입니다.' };
            }
            return { code: 200, data: cached };
        },
        cache: async (result, params, storage) => {
            if (result && result.code === 200 && result.data) {
                await cacheChecklistAggregate(storage, result.data);
            }
        }
    },
    'page.checklist.respond/save_response': {
        write: writeChecklistResponse
    },
    'page.checklist.respond/submit_response': {
        write: writeChecklistResponse
    },
    'page.checklist.result/load_result': {
        read: async (params, storage) => {
            const responseId = String((params && params.response_id) || '').trim();
            const cachedResult = responseId ? await storage.getMeta(checklistResultKey(responseId)) : null;
            const cachedResponse = responseId ? await storage.getMeta(checklistResponseKey(responseId)) : null;
            const cached = cachedResult || cachedResponse;
            if (!cached) {
                return {
                    code: 404,
                    data: null,
                    message: '캐시된 체크리스트 결과가 없습니다.'
                };
            }
            if (!await isCachedProjectSelected(storage, cachedAggregateProjectId(cached))) {
                return { code: 403, data: null, message: '현재 사용자의 MPRO 배정 캐시에 없는 체크리스트입니다.' };
            }

            if (!cached.project && cached.facility && cached.facility.project_id) {
                cached.project = await storage.get('projects', cached.facility.project_id);
            }
            return { code: 200, data: cached };
        },
        cache: async (result, params, storage) => {
            if (result && result.code === 200 && result.data && result.data.response) {
                await cacheChecklistAggregate(storage, result.data);
            }
        }
    },

    // === INSPECTION VIEWER (핵심) ===
    'page.inspection.viewer/get_viewer_data': {
        read: async (params, storage) => {
            const facilityId = normalizeCacheId(params.facility_id);
            const drawingId = normalizeCacheId(params.drawing_id);

            const facility = await getCachedRecordById(storage, 'facilities', facilityId);
            const drawing = await getCachedRecordById(storage, 'drawings', drawingId);
            const missing = [];
            if (!facility) missing.push('시설물 데이터');
            if (!drawing) missing.push('도면 데이터');
            if (missing.length > 0) {
                return {
                    code: 404,
                    data: { missing },
                    message: `오프라인 캐시에 ${missing.join(', ')}가 없습니다.`
                };
            }
            const facilityProjectId = normalizeCacheId(facility?.project_id);
            const drawingProjectId = normalizeCacheId(drawing?.project_id);
            if (!facilityProjectId || facilityProjectId !== drawingProjectId
                || !await isCachedProjectSelected(storage, facilityProjectId)) {
                return { code: 403, data: null, message: '현재 사용자의 MPRO 배정 캐시에 없는 점검 도면입니다.' };
            }
            if (!await hasCachedFacilityDrawingMapping(storage, facilityId, drawingId)) {
                return {
                    code: 404,
                    data: null,
                    message: 'M-PRO에서 해제된 시설물-도면 연결입니다.'
                };
            }
            let project = null;
            if (facility) project = await getCachedProjectById(storage, facility.project_id || drawing?.project_id);

            const damages = await storage.get('damages', drawingId);
            const annotations = await storage.get('annotations', drawingId);
            const sync = await storage.getMeta(inspectionViewerSyncKey(drawingId));
            const overviewPhotos = facilityId ? await storage.getMeta(inspectionViewerOverviewKey(facilityId)) : [];
            const pdfFile = drawingId ? await storage.get('files', `pdf_${drawingId}`) : null;
            const offlineMissing = [];
            if (!hasCachedFileData(pdfFile)) {
                offlineMissing.push({
                    category: 'drawing_pdf',
                    label: drawing.filename || drawing.name || `도면 PDF ${drawingId}`,
                    ref_id: drawingId
                });
            }

            return {
                code: 200,
                data: {
                    facility: facility || {},
                    drawing: drawing || {},
                    project: project || {},
                    damages: damages ? damages.data : [],
                    drawings: annotations ? annotations.data : [],
                    overview_photos: Array.isArray(overviewPhotos) ? overviewPhotos : [],
                    sync: sync || { workspace_id: '', revision: 0, conflict_count: 0, conflicts: [] },
                    offline_missing: offlineMissing,
                    offline: true
                }
            };
        },
        cache: async (result, params, storage) => {
            if (result && result.code === 200 && result.data) {
                const d = result.data;
                if (d.facility && d.facility.id) await mergeAndCacheFacility(storage, d.facility);
                if (d.drawing && d.drawing.id) await storage.put('drawings', d.drawing);
                if (d.project && d.project.id) await storage.put('projects', d.project);
                await cacheFacilityDrawingMapping(
                    storage,
                    params.facility_id || d.facility?.id,
                    params.drawing_id || d.drawing?.id
                );
                if (d.damages) {
                    await storage.put('damages', {
                        drawing_id: params.drawing_id,
                        data: d.damages,
                        synced_at: Date.now()
                    });
                }
                if (d.drawings) {
                    await storage.put('annotations', {
                        drawing_id: params.drawing_id,
                        data: d.drawings,
                        synced_at: Date.now()
                    });
                }
                if (d.overview_photos && params.facility_id) {
                    await storage.setMeta(inspectionViewerOverviewKey(params.facility_id), d.overview_photos);
                }
                if (d.sync && params.drawing_id) {
                    await storage.setMeta(inspectionViewerSyncKey(params.drawing_id), d.sync);
                }
            }
        }
    },
    'page.inspection.viewer/save': {
        write: async (params, storage, sync, baseuri, namespace, funcName) => {
            const drawingId = params.drawing_id;
            const projectId = normalizeCacheId(params.project_id);
            const facility = await getCachedRecordById(storage, 'facilities', params.facility_id);
            const drawing = await getCachedRecordById(storage, 'drawings', drawingId);
            if (!facility || !drawing
                || normalizeCacheId(facility.project_id) !== projectId
                || normalizeCacheId(drawing.project_id) !== projectId
                || !await isCachedProjectSelected(storage, projectId)
                || !await hasCachedFacilityDrawingMapping(storage, params.facility_id, drawingId)) {
                return { code: 403, data: null, message: '현재 사용자의 MPRO 배정 캐시에 없는 점검 도면입니다.' };
            }
            const operations = parseJson(params.operations, null);
            if (!Array.isArray(operations)) {
                return {
                    code: 400,
                    data: null,
                    message: '도면 오프라인 저장은 변경 로그(operations)가 필요합니다.'
                };
            }

            // Save damages locally
            if (params.damages) {
                let dmgData = params.damages;
                if (typeof dmgData === 'string') {
                    try { dmgData = JSON.parse(dmgData); } catch (e) { }
                }
                await storage.put('damages', {
                    drawing_id: drawingId,
                    data: dmgData,
                    localModified: Date.now()
                });
            }

            // Save drawings/annotations locally
            if (params.drawings) {
                let drwData = params.drawings;
                if (typeof drwData === 'string') {
                    try { drwData = JSON.parse(drwData); } catch (e) { }
                }
                await storage.put('annotations', {
                    drawing_id: drawingId,
                    data: drwData,
                    localModified: Date.now()
                });
            }
            if (params.overview_photos && params.facility_id) {
                await storage.setMeta(
                    inspectionViewerOverviewKey(params.facility_id),
                    parseJson(params.overview_photos, [])
                );
            }

            // Queue for sync
            await sync.queueWrite({
                entity_type: 'inspection_save',
                entity_id: drawingId,
                action: 'save',
                payload: params,
                api_endpoint: `${baseuri}/api/${namespace}/${funcName}`,
                operation_id: params.operation_id,
                client_id: params.client_id,
                base_revision: params.base_revision
            });

            return { code: 200, data: { saved: 'local', message: '로컬에 저장됨 (동기화 대기)' } };
        }
    },

    // === COMPONENT: PROJECTS ===
    'component.main.projects/data': {
        read: async (params, storage) => {
            const projects = await readCachedSelectedProjects(storage);
            return { code: 200, data: { selectedProjects: projects, selected_projects: projects } };
        },
        cache: async (result, params, storage) => {
            await cacheSelectedProjectsFromResult(result, storage);
        }
    },
    'component.main.projects/unselect': {
        write: async () => ({ code: 403, data: '용역의 PM/참여자 배정은 MPRO에서 변경해 주세요.' })
    },

    // === FACILITY COMPONENTS ===
    'component.project.facilities/get_facility_detail': {
        read: async (params, storage) => {
            return readCachedFacilityDetail(storage, params.facility_id || params.id);
        },
        cache: async (result, params, storage) => {
            if (result && result.code === 200 && result.data) {
                const data = result.data;
                const facilityId = data.facility?.id || params.facility_id || params.id;
                await cacheProjectDetailData(storage, {
                    facilities: data.facility ? [{
                        ...data.facility,
                        drawings: Array.isArray(data.drawings) ? data.drawings : data.facility.drawings
                    }] : [],
                    drawings: data.drawings
                }, {
                    scope: 'facility_links',
                    projectId: data.facility?.project_id,
                    facilityId
                });
                if (Array.isArray(data.overview_photos) && facilityId) {
                    await storage.setMeta(inspectionViewerOverviewKey(facilityId), data.overview_photos);
                }
                if (facilityId && Array.isArray(data.drawings)) {
                    for (const drawing of data.drawings) await cacheFacilityDrawingMapping(storage, facilityId, drawing.id || drawing.drawing_id);
                }
            }
        }
    },
    'page.project.detail/get_facility_detail': {
        read: async (params, storage) => {
            return readCachedFacilityDetail(storage, params.facility_id || params.id);
        },
        cache: async (result, params, storage) => {
            if (result && result.code === 200 && result.data) {
                const data = result.data;
                const facilityId = data.facility?.id || params.facility_id || params.id;
                await cacheProjectDetailData(storage, {
                    facilities: data.facility ? [{
                        ...data.facility,
                        drawings: Array.isArray(data.drawings) ? data.drawings : data.facility.drawings
                    }] : [],
                    drawings: data.drawings
                }, {
                    scope: 'facility_links',
                    projectId: data.facility?.project_id,
                    facilityId
                });
                if (Array.isArray(data.overview_photos) && facilityId) {
                    await storage.setMeta(inspectionViewerOverviewKey(facilityId), data.overview_photos);
                }
                if (facilityId && Array.isArray(data.drawings)) {
                    for (const drawing of data.drawings) await cacheFacilityDrawingMapping(storage, facilityId, drawing.id || drawing.drawing_id);
                }
            }
        }
    }
};

// Read-only API functions (used for determining if offline read is possible)
const READ_FUNCTIONS = new Set([
    'data', 'list', 'detail', 'session', 'init', 'get_viewer_data',
    'stats', 'users', 'get_facility_detail', 'download_drawing',
    'categories', 'search', 'download', 'load', 'load_response', 'load_result'
]);

export class OfflineInterceptor {
    private storage: OfflineStorage;
    private network: NetworkService;
    private sync: SyncService;

    constructor() {
        this.storage = getOfflineStorage();
        this.network = getNetworkService();
        this.sync = getSyncService();
    }

    async call(baseuri: string, namespace: string, funcName: string, data: any = {}, options: any = {}): Promise<any> {
        const handlerKey = `${namespace}/${funcName}`;
        const handler = offlineHandlers[handlerKey];
        const isRead = READ_FUNCTIONS.has(funcName);
        const url = `${baseuri}/api/${namespace}/${funcName}`;
        const browserOnline = typeof navigator === 'undefined' || navigator.onLine !== false;
        let shouldTryServer = options?.forceServer === true || this.network.isOnline || browserOnline || handlerKey === 'component.auth.login/login';
        if (!shouldTryServer && this.isNativePlatform()) {
            shouldTryServer = await this.network.checkConnectivity();
        }

        // If online, try server first. Login also retries the server because stale offline state can block online sign-in.
        if (shouldTryServer) {
            try {
                const result = await this.serverCall(url, data, options);
                this.network.setStatus('online');
                if (isProjectAssignmentDenial(handlerKey, result)) {
                    try {
                        const deniedProjectId = await resolveDeniedProjectId(this.storage, handlerKey, data);
                        if (deniedProjectId) {
                            await tombstoneDeniedProjectCache(this.storage, deniedProjectId);
                        }
                    } catch (error) {
                        console.warn('[OfflineInterceptor] Assignment tombstone failed:', error);
                    }
                }
                try {
                    await reconcileEndpointResourceTombstone(this.storage, handlerKey, result, data);
                } catch (error) {
                    console.warn('[OfflineInterceptor] Resource tombstone failed:', error);
                }
                // Cache the response
                if (handler && handler.cache) {
                    try {
                        await handler.cache(result, data, this.storage);
                    } catch (e) {
                        console.warn('[OfflineInterceptor] Cache error:', e);
                    }
                }
                return result;
            } catch (err: any) {
                const isTimeout = !!err && err.statusText === 'timeout';
                const hasHttpStatus = !!err && Object.prototype.hasOwnProperty.call(err, 'httpStatus');
                const isNetworkError = !!err && (
                    err.isNetworkError === true
                    || (hasHttpStatus && Number(err.httpStatus) === 0)
                    || isTimeout
                );
                const isAuthError = err && (err.httpStatus === 401 || err.httpStatus === 403);
                const isLogin = handlerKey === 'component.auth.login/login';
                if (isNetworkError) {
                    // A slow API response is not proof that I-PRO is offline. Confirm
                    // reachability before changing the global network/session state.
                    await this.network.checkConnectivity(true);
                }

                if (isRead && handler && handler.read && isNetworkError) {
                    const fallback = await this.tryReadFallback(handlerKey, handler, data, isNetworkError);
                    if (fallback) return fallback;
                }
                if (isNetworkError && !isRead && handler && handler.write) {
                    console.warn(`[OfflineInterceptor] Server failed, using offline write fallback for ${handlerKey}`);
                    const fallback = await handler.write(data, this.storage, this.sync, baseuri, namespace, funcName);
                    if (isLogin && fallback && Number(fallback.code) === 401 && fallback.data === OFFLINE_LOGIN_UNAVAILABLE_MESSAGE && browserOnline) {
                        return { code: 503, data: LOGIN_SERVER_UNREACHABLE_MESSAGE };
                    }
                    return fallback;
                }
                throw err;
            }
        }

        // Offline mode
        if (handler) {
            if (isRead && handler.read) {
                return handler.read(data, this.storage);
            }
            if (!isRead && handler.write) {
                return handler.write(data, this.storage, this.sync, baseuri, namespace, funcName);
            }
        }

        // No handler — return error
        return {
            code: 503,
            data: null,
            message: '오프라인 상태입니다. 이 기능은 인터넷 연결이 필요합니다.'
        };
    }

    private async tryReadFallback(
        handlerKey: string,
        handler: OfflineHandler,
        data: any,
        allowErrorResult: boolean
    ): Promise<any | null> {
        try {
            const fallback = await handler.read!(data, this.storage);
            if (fallback && (Number(fallback.code) === 200 || allowErrorResult)) {
                console.warn(`[OfflineInterceptor] Server failed, using cached fallback for ${handlerKey}`);
                return fallback;
            }
        } catch (fallbackError) {
            console.warn(`[OfflineInterceptor] Cached fallback failed for ${handlerKey}:`, fallbackError);
        }
        return null;
    }

    private async hasCachedSession(): Promise<boolean> {
        return !!(await getCachedSessionData(this.storage));
    }

    private isNativePlatform(): boolean {
        if (typeof window === 'undefined') return false;
        const capacitor = (window as any).Capacitor;
        try {
            return !!capacitor
                && typeof capacitor.isNativePlatform === 'function'
                && capacitor.isNativePlatform();
        } catch {
            return false;
        }
    }

    private sameOriginWebUrl(url: string): string | null {
        if (typeof window === 'undefined' || !window.location) return null;
        try {
            const parsed = new URL(url, window.location.href);
            if (!/^https?:$/.test(parsed.protocol) || parsed.origin !== window.location.origin) return null;
            return `${parsed.pathname}${parsed.search}${parsed.hash}`;
        } catch {
            return null;
        }
    }

    private getNativeHttp(url: string): any | null {
        const capacitor = (window as any).Capacitor;
        if (this.sameOriginWebUrl(url) || !capacitor || !capacitor.isNativePlatform || !capacitor.isNativePlatform() || !/^https?:\/\//.test(url)) {
            return null;
        }
        try {
            if (capacitor.Plugins?.CapacitorHttp?.post) return capacitor.Plugins.CapacitorHttp;
            if (capacitor.registerPlugin) return capacitor.registerPlugin('CapacitorHttp');
        } catch {}
        return null;
    }

    private encodeFormData(data: any): string {
        const body = new URLSearchParams();
        Object.keys(data || {}).forEach((key) => {
            const value = data[key];
            if (value !== undefined && value !== null) body.append(key, String(value));
        });
        return body.toString();
    }

    private serverCall(url: string, data: any, options: any): Promise<any> {
        const requestTimeout = Number(options?.timeout) > 0
            ? Number(options.timeout)
            : DEFAULT_REQUEST_TIMEOUT_MS;
        const nativeHttp = this.getNativeHttp(url);
        if (nativeHttp) {
            return nativeHttp.post({
                url,
                data: this.encodeFormData(data),
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    ...(options && options.headers ? options.headers : {})
                },
                connectTimeout: requestTimeout,
                readTimeout: requestTimeout,
                responseType: 'json'
            }).then((response: any) => {
                const httpStatus = Number(response?.status || 0);
                if (httpStatus && (httpStatus < 200 || httpStatus >= 300)) {
                    const requestError: any = new Error(`native-http: ${httpStatus}`);
                    requestError.httpStatus = httpStatus;
                    requestError.statusText = 'error';
                    throw requestError;
                }
                let payload = response ? response.data : null;
                if (typeof payload === 'string') {
                    try { payload = JSON.parse(payload); } catch {}
                }
                return payload;
            }).catch((error: any) => {
                if (error && Object.prototype.hasOwnProperty.call(error, 'httpStatus')) throw error;
                const requestError: any = error instanceof Error
                    ? error
                    : new Error(String(error?.message || error || 'native-http network error'));
                requestError.httpStatus = 0;
                requestError.statusText = error?.statusText || 'error';
                requestError.isNetworkError = true;
                throw requestError;
            });
        }

        const webRequestUrl = this.sameOriginWebUrl(url) || url;
        return new Promise((resolve, reject) => {
            $.ajax({
                url: webRequestUrl,
                type: 'POST',
                data,
                crossDomain: /^https?:\/\//.test(webRequestUrl),
                xhrFields: {
                    withCredentials: true,
                    ...(options && options.xhrFields ? options.xhrFields : {})
                },
                timeout: requestTimeout,
                ...options,
                success: (res: any) => resolve(res),
                error: (xhr: any, status: string, error: string) => {
                    const requestError: any = new Error(`${status}: ${error}`);
                    requestError.httpStatus = xhr ? xhr.status : 0;
                    requestError.statusText = status;
                    reject(requestError);
                }
            });
        });
    }
}

let _interceptorInstance: OfflineInterceptor | null = null;

export function getOfflineInterceptor(): OfflineInterceptor {
    if (!_interceptorInstance) {
        _interceptorInstance = new OfflineInterceptor();
    }
    return _interceptorInstance;
}
